import React from 'react';
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';

interface CaseResult {
  testCaseId: string;
  sourceItemTitle: string;
  pass: boolean;
  expected: {guideable: boolean; topic: string; audience: string};
  actual: {guideable: boolean; topic: string; audience: string};
  diffs: string[];
}

interface ReportData {
  id: string;
  promptName: string;
  ranAt: string;
  totalCases: number;
  passed: number;
  failed: number;
  weightedScore?: number | null;
  results: CaseResult[];
}

interface EvalCompareViewProps {
  reportA: ReportData;
  reportB: ReportData;
}

export default function EvalCompareView({reportA, reportB}: EvalCompareViewProps) {
  const pctA = reportA.weightedScore ?? (reportA.totalCases > 0 ? Math.round((reportA.passed / reportA.totalCases) * 100) : 0);
  const pctB = reportB.weightedScore ?? (reportB.totalCases > 0 ? Math.round((reportB.passed / reportB.totalCases) * 100) : 0);
  const diff = pctB - pctA;

  // Build a map of results by testCaseId
  const mapA = new Map(reportA.results.map(r => [r.testCaseId, r]));
  const mapB = new Map(reportB.results.map(r => [r.testCaseId, r]));
  const allIds = Array.from(new Set([...Array.from(mapA.keys()), ...Array.from(mapB.keys())]));

  return (
    <Box>
      {/* Summary comparison */}
      <Box sx={{display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap'}}>
        <Box sx={{flex: 1, p: 2, bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', minWidth: 200}}>
          <Typography sx={{fontSize: 11, color: '#9CA3AF', mb: 0.5}}>Report A: {reportA.promptName}</Typography>
          <Typography sx={{fontSize: 20, fontWeight: 700, color: '#111827'}}>{pctA}%</Typography>
          <Typography sx={{fontSize: 12, color: '#6B7280'}}>{reportA.passed}/{reportA.totalCases} passed</Typography>
        </Box>
        <Box sx={{flex: 1, p: 2, bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', minWidth: 200}}>
          <Typography sx={{fontSize: 11, color: '#9CA3AF', mb: 0.5}}>Report B: {reportB.promptName}</Typography>
          <Typography sx={{fontSize: 20, fontWeight: 700, color: '#111827'}}>{pctB}%</Typography>
          <Typography sx={{fontSize: 12, color: '#6B7280'}}>{reportB.passed}/{reportB.totalCases} passed</Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', px: 2}}>
          <Typography sx={{fontSize: 16, fontWeight: 600, color: diff > 0 ? '#065F46' : diff < 0 ? '#991B1B' : '#6B7280'}}>
            {diff > 0 ? '+' : ''}{diff}%
          </Typography>
        </Box>
      </Box>

      {/* Per-case comparison */}
      <TableContainer sx={{bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB'}}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{bgcolor: '#F9FAFB'}}>
              <TableCell sx={{fontSize: 12, fontWeight: 600, color: '#6B7280'}}>Title</TableCell>
              <TableCell align="center" sx={{fontSize: 12, fontWeight: 600, color: '#6B7280', width: 80}}>A</TableCell>
              <TableCell align="center" sx={{fontSize: 12, fontWeight: 600, color: '#6B7280', width: 80}}>B</TableCell>
              <TableCell align="center" sx={{fontSize: 12, fontWeight: 600, color: '#6B7280', width: 80}}>Change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allIds.map(id => {
              const a = mapA.get(id);
              const b = mapB.get(id);
              const title = a?.sourceItemTitle || b?.sourceItemTitle || id;
              const passA = a?.pass ?? null;
              const passB = b?.pass ?? null;
              const isRegression = passA === true && passB === false;
              const isImprovement = passA === false && passB === true;

              return (
                <TableRow
                  key={id}
                  sx={{
                    bgcolor: isRegression ? '#FEF2F2' : isImprovement ? '#ECFDF5' : 'transparent',
                  }}
                >
                  <TableCell sx={{fontSize: 13, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {title}
                  </TableCell>
                  <TableCell align="center" sx={{fontSize: 12}}>
                    {passA === null ? '—' : passA ? 'Pass' : 'Fail'}
                  </TableCell>
                  <TableCell align="center" sx={{fontSize: 12}}>
                    {passB === null ? '—' : passB ? 'Pass' : 'Fail'}
                  </TableCell>
                  <TableCell align="center" sx={{fontSize: 12, fontWeight: 600, color: isRegression ? '#991B1B' : isImprovement ? '#065F46' : '#6B7280'}}>
                    {isRegression ? 'Regression' : isImprovement ? 'Improved' : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
